import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Zap,
  Clock,
  Play,
  Pause,
  Trash2,
  Save
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  dynamicAnalysisApi,
  type CreateExecutionJobDto,
  type UpdateExecutionJobDto,
  EXECUTION_SCHEDULE_PRESETS,
  getScheduleLabel
} from '../../services/dynamic-analysis-api';
import { useTimezone } from '../../contexts/TimezoneContext';
import { handleApiError } from '../../services/api';
import toast from 'react-hot-toast';

const ConfigureExecutionJobPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatRelativeTime } = useTimezone();

  // Form state
  const [analysisName, setAnalysisName] = useState('');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [schedule, setSchedule] = useState('*/10 * * * *');
  const [customSchedule, setCustomSchedule] = useState('');
  const [useCustomSchedule, setUseCustomSchedule] = useState(false);

  // Fetch parent job details
  const { data: parentJobData, isLoading: parentJobLoading } = useQuery({
    queryKey: ['dynamic-analysis-job', jobId],
    queryFn: () => dynamicAnalysisApi.getJobDetails(jobId!),
    enabled: !!jobId
  });

  // Fetch existing execution job
  const { data: executionJobData, isLoading: executionJobLoading } = useQuery({
    queryKey: ['dynamic-analysis-execution-job', jobId],
    queryFn: () => dynamicAnalysisApi.getExecutionJobByParent(jobId!),
    enabled: !!jobId
  });

  const executionJob = executionJobData?.job;
  const isEditing = !!executionJob;

  // Populate form when editing
  useEffect(() => {
    if (executionJob) {
      setAnalysisName(executionJob.name || '');
      setAnalysisDescription(executionJob.config.analysisDescription || '');
      
      const preset = EXECUTION_SCHEDULE_PRESETS.find(p => p.value === executionJob.schedule);
      if (preset) {
        setSchedule(executionJob.schedule);
        setUseCustomSchedule(false);
      } else {
        setCustomSchedule(executionJob.schedule);
        setUseCustomSchedule(true);
      }
    } else if (parentJobData?.job) {
      // Set default name based on parent job
      const parentName = parentJobData.job.name;
      setAnalysisName(`${parentName} - Scheduled Execution`);
    }
  }, [executionJob, parentJobData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateExecutionJobDto) => dynamicAnalysisApi.createExecutionJob(jobId!, data),
    onSuccess: () => {
      toast.success('Scheduled analysis job created successfully');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-execution-job', jobId] });
      navigate(`/dynamic-analysis/jobs/${jobId}`);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to create scheduled analysis job');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateExecutionJobDto) => 
      dynamicAnalysisApi.updateExecutionJob(executionJob!.id, data),
    onSuccess: () => {
      toast.success('Scheduled analysis job updated successfully');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-execution-job', jobId] });
      navigate(`/dynamic-analysis/jobs/${jobId}`);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update scheduled analysis job');
    }
  });

  // Enable/disable mutation
  const toggleMutation = useMutation({
    mutationFn: async (enable: boolean) => {
      return enable 
        ? dynamicAnalysisApi.enableExecutionJob(executionJob!.id)
        : dynamicAnalysisApi.disableExecutionJob(executionJob!.id);
    },
    onSuccess: (_, enable) => {
      toast.success(`Scheduled analysis ${enable ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-execution-job', jobId] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to toggle scheduled analysis');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => dynamicAnalysisApi.deleteExecutionJob(executionJob!.id),
    onSuccess: () => {
      toast.success('Scheduled analysis job deleted');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-execution-job', jobId] });
      navigate(`/dynamic-analysis/jobs/${jobId}`);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to delete scheduled analysis job');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedSchedule = useCustomSchedule ? customSchedule : schedule;
    
    if (!selectedSchedule) {
      toast.error('Please select or enter a schedule');
      return;
    }

    const data = {
      analysis_name: analysisName,
      analysis_description: analysisDescription || undefined,
      schedule: selectedSchedule,
      enabled: true
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = parentJobLoading || executionJobLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const parentJob = parentJobData?.job;
  if (!parentJob) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Parent job not found</p>
      </div>
    );
  }

  const isActive = executionJob?.status === 'active';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/dynamic-analysis/jobs/${jobId}`)}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-warn" />
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? 'Edit Scheduled Analysis' : 'Configure Scheduled Analysis'}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            For: {parentJob.name}
          </p>
        </div>
        {isEditing && (
          <Badge variant={isActive ? 'success' : 'neutral'}>
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        )}
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-info/15 border-info/20">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-info mt-0.5" />
          <div>
            <h3 className="font-medium text-info">
              About Scheduled Analysis
            </h3>
            <p className="text-sm text-info mt-1">
              This scheduled job will automatically run statistical analysis on all dynamic entries 
              at the specified interval. This is separate from the daily job definition refresh.
            </p>
          </div>
        </div>
      </Card>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6">
          <div className="space-y-6">
            {/* Job Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Job Name
              </label>
              <input
                type="text"
                value={analysisName}
                onChange={(e) => setAnalysisName(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg 
                         bg-card text-foreground
                         focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter job name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description (Optional)
              </label>
              <textarea
                value={analysisDescription}
                onChange={(e) => setAnalysisDescription(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg 
                         bg-card text-foreground
                         focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter description"
                rows={3}
              />
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Execution Schedule
              </label>
              
              {/* Preset schedules */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {EXECUTION_SCHEDULE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => {
                      setSchedule(preset.value);
                      setUseCustomSchedule(false);
                    }}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      !useCustomSchedule && schedule === preset.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-input hover:border-primary'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Custom schedule */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="useCustomSchedule"
                  checked={useCustomSchedule}
                  onChange={(e) => setUseCustomSchedule(e.target.checked)}
                  className="w-4 h-4 text-primary rounded border-input focus:ring-ring"
                />
                <label htmlFor="useCustomSchedule" className="text-sm text-muted-foreground">
                  Use custom cron expression
                </label>
              </div>

              {useCustomSchedule && (
                <input
                  type="text"
                  value={customSchedule}
                  onChange={(e) => setCustomSchedule(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-input rounded-lg 
                           bg-card text-foreground
                           focus:ring-2 focus:ring-ring focus:border-transparent font-mono"
                  placeholder="*/10 * * * *"
                  required
                />
              )}
            </div>

            {/* Current Status (when editing) */}
            {isEditing && executionJob && (
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Current Status
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Schedule: </span>
                    <span className="text-foreground">
                      {getScheduleLabel(executionJob.schedule)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Run: </span>
                    <span className="text-foreground">
                      {executionJob.lastRunAt ? formatRelativeTime(executionJob.lastRunAt) : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <div>
              {isEditing && (
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => toggleMutation.mutate(!isActive)}
                    disabled={toggleMutation.isPending}
                  >
                    {isActive ? (
                      <>
                        <Pause className="w-4 h-4 mr-2" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Enable
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this scheduled analysis job?')) {
                        deleteMutation.mutate();
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="text-destructive hover:text-destructive/80 border-destructive/30 hover:border-destructive/40"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/dynamic-analysis/jobs/${jobId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving}
                className="bg-primary hover:bg-primary/90"
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Save Changes' : 'Create Scheduled Job'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
};

export default ConfigureExecutionJobPage;

